import { useCallback, useEffect, useRef, useState } from 'react'
import PostList from './PostList'
import { BargainUserInfo, Post } from '../../types'
import { useInfinityScroll } from '../hooks/useInfinityScroll'
import LoadingIndicator from '../layout/LoadingIndicator'

interface InfinitePostListProps {
  initialPosts: Post[]
  bargain_user_info: BargainUserInfo
  fetchMorePosts: (page: number, offset: number) => Promise<{ posts: Post[] }>
  onPostDelete?: (postId: number) => void
  onReactionChange?: () => void
  emptyMessage?: string
  className?: string
  style?: React.CSSProperties
}

const styles: Record<string, React.CSSProperties> = {
  loadingTrigger: {
    height: '20px',
    width: '100%',
    backgroundColor: 'transparent',
  },
  errorMessage: {
    textAlign: 'center',
    padding: '20px 0',
    color: '#e74c3c',
  },
  debugInfo: {
    padding: '10px',
    fontSize: '12px',
    color: '#666',
    backgroundColor: '#f0f0f0',
    margin: '10px 0',
  },
}

const InfinitePostList: React.FC<InfinitePostListProps> = ({
  initialPosts,
  bargain_user_info,
  fetchMorePosts,
  onPostDelete,
  onReactionChange,
  emptyMessage,
}) => {
  const loadMoreRef = useRef<HTMLDivElement>(null)
  const [posts, setPosts] = useState<Post[]>(initialPosts)
  const [offset, setOffset] = useState<number>(initialPosts.length)

  const fetchMore = useCallback(
    async (page: number): Promise<Post[]> => {
      const response = await fetchMorePosts(page, offset)
      // 重複排除: 既存のIDを除外
      setPosts((prevPosts) => {
        const existingIds = new Set(prevPosts.map((p) => p.id))
        const newPosts = response.posts.filter((p) => !existingIds.has(p.id))
        return [...prevPosts, ...newPosts]
      })
      setOffset((prevOffset) => prevOffset + response.posts.length)
      return response.posts
    },
    [fetchMorePosts, offset]
  )

  const { loading, hasMore, error, initializeData } = useInfinityScroll(loadMoreRef, fetchMore, {
    threshold: 0.1,
    rootMargin: '50px',
  })

  useEffect(() => {
    setPosts(initialPosts)
    setOffset(initialPosts.length)
    initializeData(initialPosts)
  }, [initialPosts, initializeData])

  const handlePostDelete = useCallback(
    (postId: number) => {
      setPosts((prevPosts) => prevPosts.filter((post) => post.id !== postId))
      setOffset((prevOffset) => prevOffset - 1)
      if (onPostDelete) {
        onPostDelete(postId)
      }
    },
    [onPostDelete]
  )

  return (
    <div>
      <PostList
        posts={posts}
        bargain_user_info={bargain_user_info}
        onPostDelete={handlePostDelete}
        onReactionChange={onReactionChange}
        emptyMessage={emptyMessage}
      />

      {posts.length > 0 && (
        <>
          {hasMore && <div ref={loadMoreRef} style={styles.loadingTrigger}></div>}

          {loading && <LoadingIndicator />}

          {error && (
            <div style={styles.errorMessage}>
              データの読み込みに失敗しました。ページを更新してください。
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default InfinitePostList
